import {
Close as CloseIcon,
Translate as TranslateIcon
} from '@mui/icons-material';
import {
Box,Button,Card,
CardContent,Chip,Dialog,DialogActions,DialogContent,DialogTitle,Divider,
IconButton,Tooltip,Typography
} from '@mui/material';
import React,{ useCallback,useEffect,useState } from 'react';

interface Term {
  id: string;
  term_arabic: string;
  term_transliteration: string;
  definition_short: string;
  definition_full: string;
  category: string;
  importance: number;
  difficulty_level: string;
}

interface TermAnnotationProps {
  text: string;
  terms: Term[];
  onTermClick?: (term: Term) => void;
}

interface TermMatch {
  term: Term;
  startIndex: number;
  endIndex: number;
  text: string;
}

const TermAnnotation: React.FC<TermAnnotationProps> = ({
  text,
  terms,
  onTermClick,
}) => {
  const [annotatedText, setAnnotatedText] = useState<React.ReactNode[]>([]);
  const [selectedTerm, setSelectedTerm] = useState<Term | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleTermClick = useCallback((term: Term) => {
    setSelectedTerm(term);
    setDialogOpen(true);
    if (onTermClick) {
      onTermClick(term);
    }
  },[onTermClick]);

  useEffect(() => {
    if (!text || !terms.length) {
      setAnnotatedText([text]);
      return;
    }

    // Находим все вхождения терминов в тексте
    const termMatches: TermMatch[] = [];

    terms.forEach(term => {
      const searchTerms = [
        term.term_arabic,
        term.term_transliteration,
        ...term.term_transliteration.split(' '), // Разбиваем транслитерацию на слова
      ].filter(Boolean);

      searchTerms.forEach(searchTerm => {
        if (searchTerm && searchTerm.length > 2) {
          const regex = new RegExp(`\\b${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'gi');
          let match;
          while ((match = regex.exec(text)) !== null) {
            termMatches.push({
              term,
              startIndex: match.index,
              endIndex: match.index + match[0].length,
              text: match[0],
            });
          }
        }
      });
    });

    // Сортируем по позиции в тексте
    termMatches.sort((a, b) => a.startIndex - b.startIndex);

    // Удаляем перекрывающиеся совпадения
    const filteredMatches: TermMatch[] = [];
    let lastEndIndex = 0;

    termMatches.forEach(match => {
      if (match.startIndex >= lastEndIndex) {
        filteredMatches.push(match);
        lastEndIndex = match.endIndex;
      }
    });

    // Создаем аннотированный текст
    const result: React.ReactNode[] = [];
    let currentIndex = 0;

    filteredMatches.forEach(match => {
      // Добавляем текст до термина
      if (currentIndex < match.startIndex) {
        result.push(text.slice(currentIndex, match.startIndex));
      }

      // Добавляем аннотированный термин
      result.push(
        <Tooltip
          key={`${match.term.id}-${match.startIndex}`}
          title={
            <Box>
              <Typography variant="subtitle2" fontWeight="bold">
                {match.term.term_arabic}
              </Typography>
              <Typography variant="body2">
                {match.term.term_transliteration}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {match.term.definition_short}
              </Typography>
            </Box>
          }
          arrow
        >
          <Chip
            label={match.text}
            size="small"
            color="primary"
            variant="outlined"
            onClick={() => handleTermClick(match.term)}
            sx={{
              cursor: 'pointer',
              fontSize: '0.75rem',
              height: '20px',
              '&:hover': {
                backgroundColor: 'primary.light',
                color: 'white',
              },
            }}
          />
        </Tooltip>
      );

      currentIndex = match.endIndex;
    });

    // Добавляем оставшийся текст
    if (currentIndex < text.length) {
      result.push(text.slice(currentIndex));
    }

    setAnnotatedText(result);
  }, [text,terms,handleTermClick]);



  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedTerm(null);
  };

  const getDifficultyColor = (level: string) => {
    switch (level.toLowerCase()) {
      case 'beginner':
        return 'success';
      case 'intermediate':
        return 'warning';
      case 'advanced':
        return 'error';
      default:
        return 'default';
    }
  };

  const getImportanceStars = (importance: number) => {
    return '⭐'.repeat(Math.min(importance, 5));
  };

  return (
    <Box>
      <Box
        sx={{
          lineHeight: 1.8,
          fontSize: '1rem',
          '& .MuiChip-root': {
            margin: '0 2px',
            verticalAlign: 'baseline',
          },
        }}
      >
        {annotatedText}
      </Box>

      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1}>
              <TranslateIcon color="primary" />
              <Typography variant="h6">
                {selectedTerm?.term_arabic}
              </Typography>
            </Box>
            <IconButton onClick={handleCloseDialog} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          {selectedTerm && (
            <Card variant="outlined">
              <CardContent>
                <Box mb={2}>
                  <Typography variant="h6" gutterBottom>
                    {selectedTerm.term_arabic}
                  </Typography>
                  <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                    {selectedTerm.term_transliteration}
                  </Typography>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box mb={2}>
                  <Typography variant="subtitle2" gutterBottom>
                    Краткое определение:
                  </Typography>
                  <Typography variant="body2" paragraph>
                    {selectedTerm.definition_short}
                  </Typography>
                </Box>

                <Box mb={2}>
                  <Typography variant="subtitle2" gutterBottom>
                    Полное определение:
                  </Typography>
                  <Typography variant="body2" paragraph>
                    {selectedTerm.definition_full}
                  </Typography>
                </Box>

                <Box display="flex" gap={2} flexWrap="wrap">
                  <Chip
                    label={selectedTerm.category}
                    color="primary"
                    variant="outlined"
                    size="small"
                  />
                  <Chip
                    label={selectedTerm.difficulty_level}
                    color={getDifficultyColor(selectedTerm.difficulty_level)}
                    variant="outlined"
                    size="small"
                  />
                  <Chip
                    label={`Важность: ${getImportanceStars(selectedTerm.importance)}`}
                    variant="outlined"
                    size="small"
                  />
                </Box>
              </CardContent>
            </Card>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseDialog} variant="outlined">
            Закрыть
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TermAnnotation;
